import { Box, ButtonBase, Collapse, Stack, Typography } from '@mui/material';
import { useState } from 'react';

import { useTranslatedText } from 'hooks/useTranslatedText';
import { type TicketAddonItem } from 'restful/tickets';
import { getAddonMaxQuantity } from 'restful/tickets/addon.utils';

import Icon from 'components/@icons/Icon';
import AmountSelector from 'components/@tickets/AmountSelector';
import { useTicketInfo } from 'components/@tickets/hooks';
import TicketPrice from 'components/@tickets/TicketPrice';

import TicketPerkList from './TicketPerkList';

interface TicketAddonRowProps {
  addon: TicketAddonItem;
  parentQuantity: number;
  quantity: number;
  canSelect: boolean;
  onChangeAddon?: (addon: TicketAddonItem, quantity: number) => void;
}

const TicketAddonRow = ({
  addon,
  parentQuantity,
  quantity,
  canSelect,
  onChangeAddon,
}: TicketAddonRowProps) => {
  const localize = useTranslatedText();
  const { originalPrice, discountedPrice } = useTicketInfo(addon.id);
  const [expanded, setExpanded] = useState(false);
  const description = localize(addon.description);
  const hasDetails = !!description || !!addon.perks?.length;
  const maxQuantity = getAddonMaxQuantity(
    addon.maxQuantityPerParentItem,
    parentQuantity,
  );

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        <ButtonBase
          disableRipple
          disabled={!hasDetails}
          aria-expanded={hasDetails ? expanded : undefined}
          onClick={(event) => {
            event.stopPropagation();
            setExpanded((isExpanded) => !isExpanded);
          }}
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: 1.5,
            flexGrow: 1,
            minWidth: 0,
            p: 0,
            textAlign: 'start',
            cursor: hasDetails ? 'pointer' : 'default',
          }}
        >
          <Box
            sx={(theme) => ({
              display: 'flex',
              color: hasDetails ? 'text.secondary' : 'grey.600',
              flexShrink: 0,
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: theme.transitions.create('transform', {
                duration: theme.transitions.duration.shortest,
              }),
            })}
          >
            <Icon name="ChevronDown" />
          </Box>
          <Stack
            sx={{
              gap: 0.5,
              minWidth: 0,
            }}
          >
            <Typography
              variant="body1"
              fontWeight="bold"
              sx={{
                color: 'common.white',
              }}
            >
              {localize(addon.name)}
            </Typography>
            <TicketPrice
              originalPrice={originalPrice}
              discountedPrice={discountedPrice}
              color="text.primary"
            />
          </Stack>
        </ButtonBase>

        {canSelect && (
          <AmountSelector
            quantity={quantity}
            isRemoveDisabled={quantity <= 0}
            isAddDisabled={
              parentQuantity <= 0 ||
              (maxQuantity !== undefined && quantity >= maxQuantity)
            }
            onRemove={() => onChangeAddon?.(addon, quantity - 1)}
            onAdd={() => onChangeAddon?.(addon, quantity + 1)}
          />
        )}
      </Box>

      <Collapse in={expanded && hasDetails}>
        <Stack
          sx={{
            gap: 2,
            pt: 2,
          }}
        >
          <TicketPerkList perks={addon.perks} />
          {!!description && (
            <Typography
              variant="small"
              sx={{
                color: 'text.secondary',
              }}
            >
              {description}
            </Typography>
          )}
        </Stack>
      </Collapse>
    </Box>
  );
};

export default TicketAddonRow;
